import { FC, useEffect, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { useTranslation } from '@frontend/i18n';
import {
  CheckboxField,
  Chip,
  NakedButton,
  PopoverDialog,
  SearchField,
  Text,
  useControlledField,
  usePopoverDialog,
} from '@frontend/design-system';
import { CategoryOption } from '../../../../../../types/form-template.types';
import {
  actionsContainerStyle,
  clearButtonStyle,
  getCheckboxListStyle,
  getTriggerButtonStyle,
  labelTextStyle,
  listItemStyle,
  multipleItemsLabelContainerStyle,
  noResultsContainerStyle,
  popoverContentStyle,
  searchFieldContainerStyle,
} from '../checklist-field/checklist-field.styles';
import {
  canSelectAll,
  clearValues,
  hasSelection,
  selectAllValues,
  toggleValue,
} from '../checklist-field/checklist-selection.util';

interface DropdownCategorizedChecklistProps {
  categoryId: string;
  options: CategoryOption[];
  fieldId: string;
  value: string[];
  onChange: (newValue: string[]) => void;
  onBlur: () => void;
  hasError: boolean;
}

const DropdownCategoryOption: FC<{
  option: CategoryOption;
  categoryId: string;
  fieldId: string;
  isChecked: boolean;
  onToggle: (optionValue: string, checked: boolean) => void;
}> = ({ option, categoryId, fieldId, isChecked, onToggle }) => {
  const checkboxProps = useControlledField({
    type: 'checkbox',
    value: isChecked,
    onChange: (checked: boolean) => onToggle(option.value ?? '', checked),
  });

  return (
    <CheckboxField
      {...checkboxProps}
      name={`${fieldId}-${categoryId}-opt-${option.value}`}
      label={option.label ?? ''}
    />
  );
};

export const DropdownCategorizedChecklist: FC<DropdownCategorizedChecklistProps> = ({
  categoryId,
  options,
  fieldId,
  value,
  onChange,
  onBlur,
  hasError,
}) => {
  const { t } = useTranslation('digital-forms-experience');
  const [searchValue, setSearchValue] = useState('');
  const [optionsToShow, setOptionsToShow] = useState<CategoryOption[]>([]);

  const { getTriggerProps, getDialogProps } = usePopoverDialog<HTMLButtonElement>({
    placement: 'bottom',
    middlewareOptions: {
      size: {
        apply({ elements, rects }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          });
        },
      },
    },
  });

  const searchFieldProps = useControlledField({
    type: 'text',
    value: searchValue,
    onChange: (val: string) => setSearchValue(val),
  });

  useEffect(() => {
    if (searchValue) {
      setOptionsToShow(
        options.filter(({ label }) => {
          if (!label) return false;
          return label.toLowerCase().includes(searchValue.toLowerCase());
        })
      );
    } else {
      setOptionsToShow(options);
    }
  }, [searchValue, options]);

  const handleToggle = (optionValue: string, checked: boolean) => {
    onChange(toggleValue(value, optionValue, checked));
    onBlur();
  };

  const handleSelectAll = () => {
    onChange(selectAllValues(value, optionsToShow));
    onBlur();
  };

  const handleClear = () => {
    onChange(clearValues(value, optionsToShow));
    onBlur();
  };

  const showSelectAll = canSelectAll(optionsToShow.length);
  const showClear = hasSelection(value, optionsToShow);

  const getLabel = () => {
    const MAX_CHIPS_TO_SHOW = 5;

    const selectedValuesInCategory = (value || []).filter((v) => options.some((o) => o.value === v));

    if (selectedValuesInCategory.length === 0) {
      return <Text css={labelTextStyle}>{t('Select')}</Text>;
    }

    const chips = selectedValuesInCategory.slice(0, MAX_CHIPS_TO_SHOW).map((v) => {
      const option = options.find((o) => o.value === v);
      return (
        <Chip key={v} variant='outline'>
          {option?.label ?? v}
        </Chip>
      );
    });

    return (
      <div css={multipleItemsLabelContainerStyle}>
        {chips}
        {selectedValuesInCategory.length > MAX_CHIPS_TO_SHOW && (
          <Text size='medium' css={labelTextStyle}>
            +{selectedValuesInCategory.length - MAX_CHIPS_TO_SHOW} {t('more')}
          </Text>
        )}
      </div>
    );
  };

  return (
    <>
      <NakedButton css={getTriggerButtonStyle(hasError)} {...getTriggerProps()}>
        {getLabel()}
      </NakedButton>

      <PopoverDialog {...getDialogProps()} css={popoverContentStyle}>
        <div css={searchFieldContainerStyle}>
          <SearchField {...searchFieldProps} name={`${fieldId}-${categoryId}-search`} placeholder={t('Search...')} />
          {optionsToShow.length > 0 && (showSelectAll || showClear) && (
            <div css={actionsContainerStyle}>
              {showSelectAll && (
                <NakedButton onClick={handleSelectAll}>
                  <Text weight='semibold'>{t('Select All')}</Text>
                </NakedButton>
              )}
              {showClear && (
                <NakedButton css={clearButtonStyle} onClick={handleClear}>
                  <Text weight='semibold'>{t('Clear')}</Text>
                </NakedButton>
              )}
            </div>
          )}
        </div>

        {optionsToShow.length === 0 ? (
          <div css={noResultsContainerStyle}>
            <Text>{t('No results found')}</Text>
          </div>
        ) : (
          <div css={getCheckboxListStyle(optionsToShow.length)}>
            <Virtuoso
              data={optionsToShow}
              itemContent={(_i, option) => {
                if (!option || !option.value) return null;
                const isChecked = (value || []).includes(option.value);

                return (
                  <div css={listItemStyle}>
                    <DropdownCategoryOption
                      option={option}
                      categoryId={categoryId}
                      fieldId={fieldId}
                      isChecked={isChecked}
                      onToggle={handleToggle}
                    />
                  </div>
                );
              }}
            />
          </div>
        )}
      </PopoverDialog>
    </>
  );
};
